import { Theme } from '@mui/material';
import { rgba } from 'polished';

interface PopupProperties {
  [key: string]: any;
}

interface PopupOptions {
  collapsible?: boolean;
  maxHeight?: number;
  layerConfig?: {
    style?: {
      labelField?: string;  // This would be "{fieldname}"
    };
    popupFields?: string[]; // Fields to show at the top of the popup
    descriptionField?: string; // Override for description field
  };
}

export const generatePopupContent = (
  properties: PopupProperties,
  theme: Theme,
  options: PopupOptions = {}
) => {
  // Default options
  const { 
    collapsible = true, 
    maxHeight = 300,
    layerConfig = {}
  } = options;

  // Special field keys that get custom treatment
  const imageKey = "image_url";
  const audioKey = "pronunciation";
  const documentKey = "documents";
  
  // Get title field from layer config style.labelField if available (removing curly braces)
  const titleKey = layerConfig.style?.labelField 
    ? layerConfig.style.labelField.replace(/[{}]/g, '')
    : "name";
    
  const descriptionKey = layerConfig.descriptionField || "description";
  
  // Priority fields to display at the top (if available in properties)
  const priorityFields = layerConfig.popupFields || [titleKey, descriptionKey];
  
  // Extract main content properties
  const title = properties[titleKey] || '';
  const imageUrl = properties[imageKey];
  const pronunciation = properties[audioKey];
  const description = properties[descriptionKey] || '';
  
  // Primary background color
  const bgColor = rgba(theme.palette.secondary.main, 0.05);
  
  // Basic styles
  const popupStyles = `
    <style>
      .popup-container {
        font-family: ${theme.typography.fontFamily};
        font-size: 14px;
        max-width: 300px;
        overflow-x: hidden;
        word-wrap: break-word;
        box-sizing: border-box;
      }
      .popup-title {
        font-weight: bold;
        color: ${theme.palette.primary.main};
        margin: 0 0 4px 0;
        font-size: 16px;
      }
      .popup-img {
        max-width: 100%;
        max-height: 160px;
        object-fit: contain;
        border-radius: 4px;
        display: block;
        margin: 4px auto;
      }
      .popup-desc {
        margin: 4px 0;
        font-size: 13px;
      }
      .details-container {
        margin-top: 8px;
        border-top: 1px solid ${rgba(theme.palette.secondary.main, 0.2)};
        padding-top: 6px;
        ${!collapsible ? `max-height: ${maxHeight}px; overflow-y: auto; overflow-x: hidden;` : ''}
      }
      .details-header {
        background-color: ${rgba(theme.palette.primary.main, 0.1)};
        color: ${theme.palette.primary.main};
        border: none;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        width: 100%;
        text-align: center;
        display: ${collapsible ? 'block' : 'none'};
        margin-bottom: 6px;
      }
      .details-content {
        display: ${collapsible ? 'none' : 'block'};
        max-height: ${maxHeight}px;
        overflow-y: auto;
        overflow-x: hidden;
        padding-top: ${collapsible ? '6px' : '0'};
      }
      .details-header:after {
        content: '+';
        float: right;
      }
      .property-row {
        display: flex;
        margin-bottom: 4px;
        font-size: 12px;
      }
      .property-label {
        font-weight: bold;
        color: ${theme.palette.primary.main};
        flex: 0 0 40%;
        padding-right: 6px;
        word-break: break-word;
      }
      .property-value {
        flex: 1;
        word-break: break-word;
        overflow-wrap: break-word;
        min-width: 0;
      }
      /* Pure CSS solution for details toggle */
      #details-toggle {
        display: none;
      }
      #details-toggle:checked + .details-header:after {
        content: '-';
      }
      #details-toggle:checked ~ .details-content {
        display: block;
      }
      .priority-field {
        margin-bottom: 6px;
      }
      .priority-label {
        font-weight: bold;
        color: ${theme.palette.primary.main};
        font-size: 13px;
      }
      .priority-value {
        font-size: 13px;
      }
    </style>
  `;
  
  // Main content HTML
  let mainContent = `
    <div class="popup-container">
      ${popupStyles}
      <h2 class="popup-title">${title}</h2>
      ${imageUrl ? `<img src="${imageUrl}" alt="${title}" class="popup-img">` : ''}
      ${pronunciation ? `<audio controls style="height:24px;width:100px;margin:4px 0"><source src="${pronunciation}" type="audio/mpeg"></audio>` : ''}
  `;

  // Add description if available
  if (description) {
    mainContent += `<p class="popup-desc">${description}</p>`;
  }
  
  // Add any priority fields (except those already shown)
  const priorityContent: string[] = [];
  
  priorityFields.forEach(field => {
    // Skip fields already shown or empty values
    if (
      field === titleKey || 
      field === descriptionKey || 
      field === imageKey || 
      field === audioKey ||
      !properties[field] ||
      properties[field] === ''
    ) {
      return;
    }
    
    const label = formatFieldName(field);
    const value = formatValue(properties[field], field);
    
    priorityContent.push(`
      <div class="priority-field">
        <div class="priority-label">${label}:</div>
        <div class="priority-value">${value}</div>
      </div>
    `);
  });
  
  if (priorityContent.length > 0) {
    mainContent += priorityContent.join('');
  }
  
  // Build details section - all fields except special ones and priority ones
  let detailsContent = '';
  let hasDetails = false;
  
  Object.entries(properties).forEach(([key, value]) => {
    // Skip fields that:
    // 1. Start with underscore
    // 2. Are already displayed at the top
    // 3. Are empty
    if (
      key.startsWith('_') || 
      priorityFields.includes(key) || 
      key === imageKey || 
      key === audioKey || 
      value === null || 
      value === undefined || 
      value === ''
    ) {
      return;
    }
    
    hasDetails = true;
    const label = formatFieldName(key);
    const displayValue = formatValue(value, key);
    
    detailsContent += `
      <div class="property-row">
        <div class="property-label">${label}</div>
        <div class="property-value">${displayValue}</div>
      </div>
    `;
  });
  
  // Only add details section if we have details
  if (hasDetails) {
    mainContent += `
      <div class="details-container">
        ${collapsible ? '<input type="checkbox" id="details-toggle">' : ''}
        ${collapsible ? '<label for="details-toggle" class="details-header">Show All Attributes</label>' : ''}
        <div class="details-content">
          ${detailsContent}
        </div>
      </div>
    `;
  }
  
  mainContent += `</div>`;
  
  return mainContent;
};

// Format field name to be more readable
const formatFieldName = (key: string): string => {
  return key
    // Remove any curly braces
    .replace(/[{}]/g, '')
    // Convert snake_case to Title Case
    .replace(/_/g, ' ')
    // Capitalize first letter of each word
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

// Format value based on type and field name
const formatValue = (value: any, key: string): string => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return '';
  }
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  // Handle objects
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return JSON.stringify(value);
  }
  
  // Special handling for documents and URLs
  if (
    (key === 'documents' || key.includes('url') || key.includes('link')) &&
    typeof value === 'string' &&
    (value.startsWith('http') || value.startsWith('/'))
  ) {
    return `<a href="${value}" target="_blank" rel="noopener noreferrer">View</a>`;
  }
  
  // Default - return as string
  return String(value);
};
